import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import textCss from '../../../../components/form/elements/text.module.css';
import css from '../index.module.css';
import { IBudgetItem } from '../blueprint';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  item: IBudgetItem;
  dataName?: string;
}

export default function TableRow({ item, dataName }: Props) {
  const { t } = useTranslation();
  const formMethods = useFormContext();
  const { register, control, setValue, getValues } = formMethods;
  const [isDisabled, setIsDisabled] = useState(false);
  const styles = useMemo(() => {
    return css['table-row'].concat(
      ' ',
      css[`row-lvl-${item.level}`],
      ' ',
      item.uppercase ? 'uppercase' : '',
      ' ',
      item.hideBorder ? css['no-border'] : ''
    );
  }, [item]);

  const handleChange = useCallback(
    (event: any) => {
      if (event.target.value && event.target.value.length) {
        const inputValue = parseFloat(event.target.value || 0);
        const formattedValue = parseFloat(inputValue.toFixed(2));
        setValue(item.name, formattedValue);

        // fix for zeros in start (01,11), but bug when need zero after comma (1,0|2)
        // event.target.value = formattedValue;
      } else {
        setValue(item.name, 0);
      }
    },
    [item.name, setValue]
  );

  const watchValues = useWatch({
    name: item.calculable,
    disabled: !item.calculable,
  });
  useEffect(() => {
    function setSum(sum: number) {
      sum = parseFloat(sum.toFixed(2));
      if (getValues(item.name) !== sum) {
        if (sum !== 0) {
          setValue(item.name, sum);
        } else {
          // remove this out if you need to save last value (from last erased children)
          if (isDisabled) {
            setValue(item.name, 0);
          }
        }
      }
      setIsDisabled(sum !== 0);
    }

    if (watchValues && watchValues.length) {
      if (item.name === 'gross_profit') {
        const sum = watchValues[0] - watchValues[1] || 0;
        return setSum(sum);
      }
      if (item.name === 'gross_profitability') {
        let sum = 0;
        if (watchValues[2] && watchValues[2] !== 0) {
          sum = ((watchValues[0] - watchValues[1]) / watchValues[2]) * 100;
        }
        return setSum(sum);
      }
      if (
        item.name === 'EBITDA' ||
        item.name === 'loan_payment' ||
        item.name === 'net_profit' ||
        item.name === 'without_area_payments'
      ) {
        const sum = watchValues[0] - watchValues[1] || 0;
        return setSum(sum);
      }
      if (item.name === 'EBIT') {
        const sum =
          watchValues[0] - watchValues[1] - watchValues[2] - watchValues[3] ||
          0;
        return setSum(sum);
      }
      if (item.name === 'DSCR') {
        let sum = 0;
        if (watchValues[1] && watchValues[1] !== 0) {
          sum = watchValues[0] / watchValues[1];
        }
        return setSum(sum);
      }

      const sum = watchValues.reduce((accumulator, currentValue) => {
        if (typeof currentValue === 'number') {
          return accumulator + currentValue;
        }
        return accumulator;
      }, 0);
      setSum(sum);
    }
  }, [item.name, setValue, getValues, watchValues, isDisabled]);

  return (
    <div className={styles} data-name={dataName}>
      {item.children ? (
        <div
          className={css['table-data']}
          data-tip
          data-for={`${item.name}-tooltip`}
        >
          {t(item.title)}
          <ReactTooltip
            id={`${item.name}-tooltip`}
            backgroundColor={'#002A58'}
            place="top"
            effect="solid"
          >
            {t('pages.finance.budget.click_to_show')}
          </ReactTooltip>
        </div>
      ) : (
        <div className={css['table-data']}>{t(item.title)}</div>
      )}
      <div className={css['table-data']}>
        <Controller
          name={item.name}
          control={control}
          render={({ field }) => {
            return (
              <div className={textCss['input-container']}>
                <input
                  type="number"
                  name={item.name}
                  data-name={item.name + '_input'}
                  className={textCss['input']}
                  min="0"
                  step=".01"
                  {...register(item.name)}
                  {...field}
                  disabled={isDisabled || item.disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onChange={handleChange}
                />
              </div>
            );
          }}
        />
      </div>
      <div className={css['table-data']}>
        {item.children ? <FontAwesomeIcon icon={faChevronDown} /> : ''}
      </div>
    </div>
  );
}
